import { defineStore } from "pinia";
import {
  DataBoundaryGeometry,
  useApi,
  useDataSource,
  useGammaData,
} from "@/services";
import { useAxios } from "@/config";
import { DatasetRecord, GDT } from "@/types";
import { AxiosResponse } from "axios";
/*
interface HitCodeUpdateRequest {
  columnInserts: ColumnInsert[];
}*/

export interface HitCode {
  hitcode: number;
  description: string;
  shortCode: string;
}

export const HIT_CODES = [
  {
    hitcode: -700,
    description: "Unable to Locate",
    shortCode: "X",
  },
  {
    hitcode: -500,
    description: "Temporarily placed",
    shortCode: "X",
  },
  {
    hitcode: -400,
    description: "Manual Town/Townland",
    shortCode: "T",
  },
  {
    hitcode: -300,
    description: "Discontinued",
    shortCode: "X",
  },
  {
    hitcode: -200,
    description: "Manual Building",
    shortCode: "B",
  },
  {
    hitcode: -100,
    description: "Manual Street/Industrial estate",
    shortCode: "S",
  },
  {
    hitcode: 0,
    description: "Unknown",
    shortCode: "X",
  },
  {
    hitcode: 2,
    description: "Address Point",
    shortCode: "B",
  },
  {
    hitcode: 3,
    description: "Organization",
    shortCode: "B",
  },
  {
    hitcode: 4,
    description: "Building",
    shortCode: "B",
  },
  {
    hitcode: 6,
    description: "Street",
    shortCode: "S",
  },
  {
    hitcode: 7,
    description: "Townland ",
    shortCode: "T",
  },
  {
    hitcode: 9,
    description: "County",
    shortCode: "X",
  },
  {
    hitcode: 100,
    description: "Building",
    shortCode: "B",
  },
  {
    hitcode: 101,
    description: "Sub-Building",
    shortCode: "B",
  },
  {
    hitcode: 102,
    description: "Organisation",
    shortCode: "B",
  },
  {
    hitcode: 105,
    description: "Postcode",
    shortCode: "B",
  },
  {
    hitcode: 109,
    description: "Street Complete",
    shortCode: "S",
  },
  {
    hitcode: 110,
    description: "Street",
    shortCode: "S",
  },
  {
    hitcode: 111,
    description: "Terrace",
    shortCode: "S",
  },
  {
    hitcode: 112,
    description: "Apartment Complex",
    shortCode: "B",
  },
  {
    hitcode: 113,
    description: "Educational Campus",
    shortCode: "B",
  },
  {
    hitcode: 114,
    description: "Hospital",
    shortCode: "B",
  },
  {
    hitcode: 115,
    description: "Nursing Home",
    shortCode: "B",
  },
  {
    hitcode: 117,
    description: "Court",
    shortCode: "S",
  },
  {
    hitcode: 118,
    description: "Halting Site",
    shortCode: "B",
  },
  {
    hitcode: 119,
    description: "Flat Complex",
    shortCode: "B",
  },
  {
    hitcode: 120,
    description: "Housing Estate",
    shortCode: "S",
  },
  {
    hitcode: 121,
    description: "Sheltered Housing",
    shortCode: "B",
  },
  {
    hitcode: 123,
    description: "School",
    shortCode: "B",
  },
  {
    hitcode: 126,
    description: "Townhouses",
    shortCode: "S",
  },
  {
    hitcode: 127,
    description: "Private Estate",
    shortCode: "S",
  },
  {
    hitcode: 128,
    description: "Religous Organisation",
    shortCode: "B",
  },
  {
    hitcode: 129,
    description: "Group of Detached Houses",
    shortCode: "S",
  },
  {
    hitcode: 130,
    description: "Townland",
    shortCode: "T",
  },
  {
    hitcode: 131,
    description: "Townland Incomplete",
    shortCode: "T",
  },
  {
    hitcode: 135,
    description: "Townland Near",
    shortCode: "T",
  },
  {
    hitcode: 140,
    description: "Industrial Estate",
    shortCode: "S",
  },
  {
    hitcode: 145,
    description: "Business Park",
    shortCode: "S",
  },
  {
    hitcode: 146,
    description: "Commercial & Residential Development",
    shortCode: "S",
  },
  {
    hitcode: 147,
    description: "Holiday Homes",
    shortCode: "S",
  },
  {
    hitcode: 150,
    description: "Shopping Centre",
    shortCode: "B",
  },
  {
    hitcode: 200,
    description: "Nearby Street",
    shortCode: "S",
  },
  {
    hitcode: 201,
    description: "0",
    shortCode: "S",
  },
  {
    hitcode: 300,
    description: "Village",
    shortCode: "T",
  },
  {
    hitcode: 390,
    description: "Urban Area",
    shortCode: "X",
  },
  {
    hitcode: 400,
    description: "Town Centre",
    shortCode: "T",
  },
  {
    hitcode: 401,
    description: "0",
    shortCode: "T",
  },
  {
    hitcode: 600,
    description: "Not Geocoded",
    shortCode: "X",
  },
  {
    hitcode: 650,
    description: "Invalid or Partial Postcode",
    shortCode: "X",
  },
  {
    hitcode: 666,
    description: "Interactive Found",
    shortCode: "B",
  },
  {
    hitcode: 800,
    description: "Foreign Address",
    shortCode: "X",
  },
  {
    hitcode: 900,
    description: "Invalid Address",
    shortCode: "X",
  },
  {
    hitcode: 1100,
    description: "Could Not Parse",
    shortCode: "X",
  },
  {
    hitcode: 80400,
    description: "Urban Centre",
    shortCode: "X",
  },
  {
    hitcode: 90400,
    description: "City Centre",
    shortCode: "X",
  },
];

interface HitcodeState {
  selectedHitCode: undefined | HitCode;
  selectedRecord: undefined | DatasetRecord;
  tableDefinition: null | GDT.TableDefinition;
  data: null | GDT.Data;
}

export const useDataHitcodes = defineStore("hitcodes", {
  state: (): HitcodeState => ({
    selectedHitCode: undefined,
    selectedRecord: undefined,
    tableDefinition: null,
    data: null,
  }),
  actions: {
    setSelected(hitcode: number | undefined) {
      this.selectedHitCode = hitcode
        ? HIT_CODES.find((h) => h.hitcode === hitcode)
        : undefined;
    },
  },
});
